/**
 * RefreshCoordinator - Proactive Token Refresh
 *
 * Schedules proactive token refresh 5 minutes before expiry.
 * This is an OPTIMIZATION to prevent 401s - it NEVER triggers logout.
 *
 * Key behaviors:
 * 1. Schedules refresh before token expires
 * 2. Clears timer when user logs out
 * 3. Retries with exponential backoff on failure
 * 4. Refreshes when tab regains focus
 * 5. Handles multi-tab sign-in and logout via storage events
 * 6. Keeps asking for a session the backend gave no verdict on at page load
 *
 * CRITICAL: Proactive refresh failures just retry.
 * The axios interceptor handles actual auth failures.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { refreshToken, restoreSession } from '@/utils/tokenRefresh.js';
import { authController } from '@/utils/authController.js';
import { createLogger } from '@/utils/logger';

const logger = createLogger('RefreshCoordinator');

const INITIAL_RETRY_DELAY = 30000; // 30 seconds
const MAX_RETRY_DELAY = 120000; // 2 minutes
const REFRESH_BUFFER_MINUTES = 5;
const MIN_SCHEDULE_MS = 10000; // 10 seconds minimum

/** The next retry waits twice as long, up to MAX_RETRY_DELAY. */
const backOff = (retryDelayRef) => {
  retryDelayRef.current = Math.min(retryDelayRef.current * 2, MAX_RETRY_DELAY);
};

/**
 * RefreshCoordinator component
 *
 * Wrap your app with this component to enable proactive token refresh.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function RefreshCoordinator({ children }) {
  // Track authentication state using authController as single source of truth
  const [hasToken, setHasToken] = useState(() => authController.isAuthenticated());
  const timerRef = useRef(null);
  const retryDelayRef = useRef(INITIAL_RETRY_DELAY);
  // Refs break the circular dependency between scheduling and the jobs that
  // schedule their own next run
  const doProactiveRefreshRef = useRef(null);
  const doRestoreRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // One timer runs the next job: a proactive refresh, or asking again for a
  // session the backend gave no verdict on.
  const schedule = useCallback(
    (jobRef, delayMs) => {
      clearTimer();
      timerRef.current = setTimeout(() => jobRef.current(), delayMs);
    },
    [clearTimer]
  );

  const scheduleRefresh = useCallback(
    (delayMs) => schedule(doProactiveRefreshRef, delayMs),
    [schedule]
  );

  const doProactiveRefresh = useCallback(async () => {
    try {
      await refreshToken();
      retryDelayRef.current = INITIAL_RETRY_DELAY; // Reset on success

      // Schedule next refresh
      const tokenInfo = authController.getTokenInfo();
      if (tokenInfo.minutesUntilExpiry > 0) {
        const nextRefreshMs = Math.max(
          (tokenInfo.minutesUntilExpiry - REFRESH_BUFFER_MINUTES) * 60 * 1000,
          MIN_SCHEDULE_MS
        );
        scheduleRefresh(nextRefreshMs);
      }
    } catch {
      // NEVER logout from proactive refresh - just retry with backoff
      scheduleRefresh(retryDelayRef.current);
      backOff(retryDelayRef);
    }
  }, [scheduleRefresh]);

  // A page whose session the backend gave no verdict on at load (a deploy
  // restarting it, a dropped network, staging's gate) rendered without a
  // token, and a signed-out page asks nothing. It keeps asking here, on the
  // proactive refresh's backoff, and NEVER signs anyone out: restoreSession
  // ends a session only on the backend's 401/403, and then there is
  // nothing left to ask for.
  const doRestore = useCallback(async () => {
    const outcome = await restoreSession();
    if (outcome === 'signed-in') {
      retryDelayRef.current = INITIAL_RETRY_DELAY;
      setHasToken(true);
      return;
    }
    if (outcome === 'unreachable') {
      schedule(doRestoreRef, retryDelayRef.current);
      backOff(retryDelayRef);
    }
  }, [schedule]);

  // Keep the refs in sync with the latest job callbacks
  useEffect(() => {
    doProactiveRefreshRef.current = doProactiveRefresh;
  }, [doProactiveRefresh]);

  useEffect(() => {
    doRestoreRef.current = doRestore;
  }, [doRestore]);

  // Handle tab visibility - refresh when tab gains focus
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && hasToken) {
      const tokenInfo = authController.getTokenInfo();
      if (
        tokenInfo.hasToken &&
        tokenInfo.minutesUntilExpiry <= REFRESH_BUFFER_MINUTES
      ) {
        doProactiveRefresh();
      }
    }
  }, [hasToken, doProactiveRefresh]);

  // Main effect - schedule refresh when we have a token
  useEffect(() => {
    if (!hasToken) {
      clearTimer();
      retryDelayRef.current = INITIAL_RETRY_DELAY;
      if (!authController.isAuthenticated() && authController.mayHaveSession()) {
        schedule(doRestoreRef, retryDelayRef.current);
        backOff(retryDelayRef);
        return clearTimer;
      }
      return;
    }

    // hasToken turns true only just after a token was confirmed held (at
    // mount, a restore, a pick-up from another tab), so the page holds one
    // here. minutesUntilExpiry rounds: a live token with under 30 seconds
    // left reads as 0.
    const tokenInfo = authController.getTokenInfo();
    if (tokenInfo.minutesUntilExpiry <= 0) {
      // Token expired - refresh immediately
      doProactiveRefresh();
    } else if (tokenInfo.minutesUntilExpiry <= REFRESH_BUFFER_MINUTES) {
      // Token expiring soon - refresh soon
      const refreshInMs = Math.max(
        (tokenInfo.minutesUntilExpiry - 1) * 60 * 1000,
        MIN_SCHEDULE_MS
      );
      scheduleRefresh(refreshInMs);
    } else {
      // Schedule refresh before expiry
      const refreshInMs = Math.max(
        (tokenInfo.minutesUntilExpiry - REFRESH_BUFFER_MINUTES) * 60 * 1000,
        MIN_SCHEDULE_MS
      );
      scheduleRefresh(refreshInMs);
    }

    return clearTimer;
  }, [hasToken, clearTimer, schedule, scheduleRefresh, doProactiveRefresh]);

  // Visibility change listener
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleVisibilityChange]);

  // Multi-tab sync - listen for sign-in and logout in other tabs. Every tab
  // once read one stored token; since #556 "Migrate web refresh token to
  // HttpOnly cookie" each holds its own in memory,
  // and the session flag says what the stored token used to. Signed out
  // elsewhere: this tab's token goes. Signed in elsewhere: this tab gets
  // its own from the cookie. Then the same as before: follow the result.
  // The event fires only when the flag's value changes, so a refresh in
  // another tab is silent.
  useEffect(() => {
    const handleStorageChange = async (e) => {
      if (!authController.isSessionFlagChange(e)) return;
      if (e.newValue === null) {
        authController.forgetAccessToken();
      } else if (!authController.isAuthenticated()) {
        try {
          await refreshToken();
        } catch (error) {
          logger.warn({ status: error.status, err: error }, 'session from another tab could not be picked up');
        }
      }
      const isAuth = authController.isAuthenticated();
      setHasToken(isAuth);
      if (!isAuth) {
        clearTimer();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [clearTimer]);

  return children;
}

export default RefreshCoordinator;
